import { isDevelopment } from "@app/types/shared/env";

// One AWU credit represents $0.0085, or 8,500 microdollars, of raw model cost.
export const MODEL_COST_MICRO_USD_PER_AWU_CREDIT = 8_500;

// Metronome product and billable metric IDs per environment.
// Generated by metronome_setup.ts, stable across runs.

// --- DEV (sandbox) ---

// Metrics
const DEV_METRIC_LLM_PROVIDER_COST_PROGRAMMATIC =
  "e02846b3-956c-48bc-9fd1-162061aed624";
const DEV_METRIC_TOOL_INVOCATIONS_V2 = "0a35a534-0d1c-4ed5-8177-358d9dff71a5";
const DEV_METRIC_LLM_PROVIDER_COST_AWU_V2 =
  "a6abefc5-d749-4757-a139-336b53c837f5";

// Products
const DEV_PRODUCT_PROGRAMMATIC_USAGE = "daaf92ec-d0a7-444d-972e-a2d48c7edd0c";
const DEV_PRODUCT_AI_USAGE = "8b2baf8c-5edc-451d-877f-0eeef554afa9";
const DEV_PRODUCT_TOOL_USAGE = "995bcf0a-801f-4883-bde4-ef6ff39ffbb3";
const DEV_PRODUCT_WORKSPACE_SEAT = "e1532e1d-4964-4656-b6db-070fafafc44c";
const DEV_PRODUCT_PRO_SEAT = "db03586f-9e8d-4978-9a62-f51193182be3";
const DEV_PRODUCT_MAX_SEAT = "51b4ea2f-c4ed-4903-aba6-486d0bf61ac0";
const DEV_PRODUCT_FREE_SEAT = "e3302b51-66af-40ad-ac0e-544fc38ccfa9";
const DEV_PRODUCT_FREE_CREDITS = "04f41dd1-ba27-42e3-93d5-6121712a4b67";
const DEV_PRODUCT_EXCESS_CREDITS = "7e74d48b-44be-4e9c-9543-7136e6da8d96";
const DEV_PRODUCT_PREPAID_COMMIT = "5f4331b7-4bf6-488b-9a0c-51bd139ac91c";
const DEV_PRODUCT_SEAT_SUBSCRIPTION_CREDITS =
  "92f3466b-2423-4f2a-a8d1-0f6e9f7f0140";
const DEV_PRODUCT_SEAT_SUBSCRIPTION_COMMIT =
  "475eb7dd-5043-49bc-96eb-1b08f94fda51";
const DEV_PRODUCT_PLATFORM_FEE = "11e635e1-0fb9-44e1-9499-690d5ee27ace";

// --- PROD (production) — TODO: update after running setup script in production ---

// Metrics
const PROD_METRIC_LLM_PROVIDER_COST_PROGRAMMATIC =
  "3ad68bfb-7a59-49ad-b29e-54700fa0e6fa";
const PROD_METRIC_TOOL_INVOCATIONS_V2 = "f32cc30e-c52f-4dc0-bde0-cef343bdc6d3";
const PROD_METRIC_LLM_PROVIDER_COST_AWU_V2 =
  "ffb1ecce-6c2b-4f16-add1-5da81baec0a7";

// Products
const PROD_PRODUCT_PROGRAMMATIC_USAGE = "cb21a6da-9790-4ab4-bb2d-0a82d5fdf4f3";
const PROD_PRODUCT_AI_USAGE = "24f8b558-5ad1-4c41-890a-0e6ccf3ba0ea";
const PROD_PRODUCT_TOOL_USAGE = "d44f373d-730f-424f-8866-241b61157b68";
const PROD_PRODUCT_WORKSPACE_SEAT = "5c2e2986-1305-4406-96a2-2296e66b5a25";
const PROD_PRODUCT_PRO_SEAT = "19934ee2-fcf4-40a2-a57a-24f75b0c2b22";
const PROD_PRODUCT_MAX_SEAT = "8a92cf6a-8e0b-416c-9b46-9c715eff0c89";
const PROD_PRODUCT_FREE_SEAT = "198511a4-2b27-44b3-b1d1-9839ae6c2b23";
const PROD_PRODUCT_FREE_CREDITS = "7379999c-5492-4e68-968f-345a26f6da63";
const PROD_PRODUCT_EXCESS_CREDITS = "26f0023b-e123-4154-a82d-104451fba068";
const PROD_PRODUCT_PREPAID_COMMIT = "1408c9fc-dea1-4269-bd6d-1bc0aa1f1218";
const PROD_PRODUCT_SEAT_SUBSCRIPTION_CREDITS =
  "03faa744-c1ab-4b94-aaf1-5fdaec89e11a";
const PROD_PRODUCT_SEAT_SUBSCRIPTION_COMMIT =
  "5cdb49e6-0a92-4a55-9c92-c8913b2df0d5";
const PROD_PRODUCT_PLATFORM_FEE = "704ea5b4-6680-4618-a154-b2343d78a1e2";

// --- Credit type IDs (stable across envs unless noted) ---

// USD, EUR and GBP are the same in sandbox and production.
export const CREDIT_TYPE_USD_ID = "2714e483-4ff1-48e4-9e25-ac732e8f24f2";
export const CREDIT_TYPE_EUR_ID = "58f0be15-cc47-4220-bdaf-072ab0e44f96";
export const CREDIT_TYPE_GBP_ID = "0f99b795-a801-4653-ad4b-b99922be625d";

export const PLAN_CODE_CUSTOM_FIELD_KEY = "DUST_PLAN_CODE";

// Custom field stamped on payment-gated contracts to identify the activation
// flow type. The contract.start webhook checks this field and skips the
// automatic subscription swap when set — payment_gate.payment_status handles it.
export const PAYMENT_GATE_TYPE_CUSTOM_FIELD_KEY = "DUST_PAYMENT_GATE_TYPE";
export const PAYMENT_GATE_TYPE_SUBSCRIPTION_ACTIVATION =
  "subscription_activation";

// Custom field stamped on contracts whose corresponding DB subscription row
// is created synchronously by the calling code right after provisioning the
// contract (e.g. `provisionCreditPricedFreePlan`), instead of relying on this
// webhook to do it. Without this, the synchronous write and contract.start's
// own subscription-swap logic can both read the workspace's active
// subscription concurrently and each end it + insert a new active row,
// leaving the workspace with two active subscriptions. The contract.start
// webhook checks this field and skips its subscription swap entirely when set.
export const SUBSCRIPTION_SWAP_HANDLED_INLINE_CUSTOM_FIELD_KEY =
  "DUST_SUBSCRIPTION_SWAP_INLINE";

// Custom field stamped on every seat-style product (Workspace / Pro / Max /
// Free / future seat tiers). Value is the membership seat type ("workspace"
// | "pro" | "max" | "free"). Lets runtime code identify which subscription
// bills which seat type without comparing product names or IDs (both of
// which can change on redeploy).
export const SEAT_TYPE_CUSTOM_FIELD_KEY = "DUST_SEAT_TYPE";

// Product-level custom field carrying the Stripe product ID Metronome
// should reference when invoicing this product. Populated manually in the
// Metronome UI per product; read by Metronome (via the
// `invoiceitem.price.product` mapping in the Stripe integration settings)
// when generating Stripe invoices for payment-gated commits — without it,
// the payment-gate workflow fails with "Failed to get Stripe product ID
// for Metronome Product '…'".
export const STRIPE_PRODUCT_ID_CUSTOM_FIELD_KEY = "STRIPE_PRODUCT_ID";

// Custom field stamped on contract credits AND commits to identify whether they
// belong to the workspace pool ("pool") or are excess-overage accounting credits
// ("excess"). The pool balance alert carries per-entity ContractCredit + Commit
// filters that both match "pool", so pool credits and pool commits count toward
// the balance while excess credits are excluded. The key must be shared across
// both entities: Metronome rejects an alert whose custom_field_filters use
// different key/value pairs per entity type ("must specify the same set of key
// and value pairs for every entity type"). Commits only ever take "pool" (they
// have no excess variant).
export const CONTRACT_CREDIT_TYPE_CUSTOM_FIELD_KEY =
  "DUST_CONTRACT_CREDIT_TYPE";
export const CONTRACT_CREDIT_TYPE_EXCESS = "excess";
export const CONTRACT_CREDIT_TYPE_POOL = "pool";
// Per-user free-seat credits. Stamped at creation so they're explicitly typed
// (not just "unstamped") and excluded from the pool balance — the pool filter
// matches "pool", so "free_seat" never counts. Lets the credit.create /
// credit.segment.start webhook leave them alone via its existing
// already-stamped early-return, with no per-credit special-casing.
export const CONTRACT_CREDIT_TYPE_FREE_SEAT = "free_seat";

export type ContractCreditType =
  | typeof CONTRACT_CREDIT_TYPE_EXCESS
  | typeof CONTRACT_CREDIT_TYPE_POOL
  | typeof CONTRACT_CREDIT_TYPE_FREE_SEAT;

// Custom field stamped on the non-recurring, contract-level commits/credits
// whose unused balance must survive a RENEWAL transition (initial credits,
// AWU top-ups, business-activation seat prepayment). On renewal the
// `contract.start` webhook reads the source contract's flagged entries, derives
// the balance left at the transition from their expiration ledger entry, and
// re-grants it on the successor contract (see `carryOverContractBalancesOnRenewal`).
//
// The field VALUE is an ISO timestamp (the entry's absolute expiry, one year
// after its start — see `oneYearAfter`). Legacy entries may instead carry
// `CARRY_ON_RENEWAL_FOREVER_VALUE` (never expires); the carry-over reader treats
// any non-date value as forever. Metronome clamps a commit's live access window
// to the contract end when a RENEWAL ends the source, so by the time the webhook
// runs the original expiry is gone — we stamp it here so it survives. The
// carried grant re-stamps the same value, preserving the expiry across any
// number of renewals.
//
// This replaces Metronome's `rollover_fraction`: a rolled-over commit becomes a
// "rollover" commit, and Metronome consumes rollover commits before all prepaid
// commits regardless of priority ("commit type takes precedence over priority").
// Since these are non-seat-based, that ordering trips our client-level rule that
// a non-seat-based commit may not be prioritized over a seat-based one, so the
// transition is rejected. Re-granting as a plain prepaid commit keeps it in the
// prepaid tier, ordered after the seat allocation by priority.
export const CARRY_ON_RENEWAL_CUSTOM_FIELD_KEY = "DUST_CARRY_ON_RENEWAL";

export const CARRY_ON_RENEWAL_FOREVER_VALUE = "forever";

// Custom fields stamped on AWU pool commits (admin grants via
// `grantAwuCreditsPlugin` and self-serve top-ups via `addPaymentGatedCommitToContract`)
// for finance reconciliation against the Stripe invoice Metronome generates.
export const AWU_PURCHASE_ORDER_ID_CUSTOM_FIELD_KEY = "DUST_PURCHASE_ORDER_ID";
export const AWU_AMOUNT_CUSTOM_FIELD_KEY = "DUST_AWU_AMOUNT";
export const AWU_DISCOUNT_PERCENT_CUSTOM_FIELD_KEY =
  "DUST_AWU_DISCOUNT_PERCENT";

export const FOREVER_ENDING_BEFORE = new Date("2999-01-01T00:00:00.000Z");

export function oneYearAfter(start: Date): Date {
  const end = new Date(start);
  end.setUTCFullYear(end.getUTCFullYear() + 1);
  return end;
}

// Custom field stamped on a per-user (free) seat credit, carrying the seat's
// user sId. Metronome alerts can filter on custom fields but not on a credit's
// presentation specifier, so this is what lets a per-user
// `low_remaining_contract_credit_balance_reached` alert fire as each free
// user depletes their individual credit. The key must be registered with
// Metronome (see `CUSTOM_FIELD_KEYS` in `scripts/metronome_setup.ts`) before it
// can be stamped, or the create is rejected with "Invalid custom field keys".
export const PER_USER_CREDIT_USER_CUSTOM_FIELD_KEY =
  "DUST_PER_USER_CREDIT_USER";

// Prefix applied to user sIds when emitting Metronome usage events for users on
// a free seat, and when creating their per-user credits and alerts. This
// decorrelates free-seat credit consumption from regular usage: the free credit
// specifier filters on `presentation_group_values.user_id = "free-<sId>"`, so
// it only drains against events emitted with that exact value.
const FREE_SEAT_METRONOME_USER_ID_PREFIX = "free-";

export function toFreeMetronomeUserId(sId: string): string {
  return `${FREE_SEAT_METRONOME_USER_ID_PREFIX}${sId}`;
}

// Strip the free-seat prefix from a Metronome user id to recover the raw sId.
// Returns null when the value doesn't carry the prefix (i.e. not a free-seat id).
export function fromFreeMetronomeUserId(
  metronomeUserId: string
): string | null {
  if (metronomeUserId.startsWith(FREE_SEAT_METRONOME_USER_ID_PREFIX)) {
    return metronomeUserId.slice(FREE_SEAT_METRONOME_USER_ID_PREFIX.length);
  }
  return null;
}

// Pricing/billable-metric group key that splits AWU usage into "user",
// "programmatic", and "free" slices. Emitted on every usage event and used
// by pricing rules, per-user reporting, and spend-threshold alerts.
export const USAGE_TYPE_GROUP_KEY = "usage_type";
export const USAGE_TYPE_USER = "user";
export const USAGE_TYPE_PROGRAMMATIC = "programmatic";
export const USAGE_TYPE_FREE = "free";

// Custom field key used to store the HubSpot deal ID on a Metronome contract.
// Must be registered in Metronome before it can be stamped.
export const HUBSPOT_DEAL_ID_CUSTOM_FIELD_KEY = "HUBSPOT_DEAL_ID";

// Custom field key stamped on a (future-dated) contract by the legacy → Business
// migration. Its presence tells the `contract.start` webhook to, at activation
// time, convert the workspace's remaining convertible legacy credits to AWU and
// grant the per-user free AWU bonus. The value is the free AWU granted per
// workspace member (a stringified integer). Must be registered in Metronome
// before it can be stamped.
export const LEGACY_CREDIT_MIGRATION_CUSTOM_FIELD_KEY =
  "DUST_LEGACY_CREDIT_MIGRATION";

// Suffix appended to the annual variant of each seat product name in
// Metronome (e.g. "Pro Seat (Yearly)"). Used by the setup script when
// declaring products and by the UI to strip the suffix from displayed seat
// labels — the billing cadence is conveyed by the tab selector.
export const SEAT_PRODUCT_YEARLY_SUFFIX = " (Yearly)";

// AWU (Agentic Work Units) differs per environment.
export const DEV_CREDIT_TYPE_AWU_ID = "eb003cc7-4935-467d-a41c-c1738c1c9dc2";
export const PROD_CREDIT_TYPE_AWU_ID = "8dfc9846-a38e-44f8-a625-bd9372af681c";

// Programmatic USD differs per environment.
export const DEV_CREDIT_TYPE_PROG_USD_ID =
  "713dda3d-4e9c-456f-91cf-c79cf5b71412";
export const PROD_CREDIT_TYPE_PROG_USD_ID =
  "db4b2912-4dfc-43ee-a910-1a894b89fe60";

/** Map Stripe currency code to Metronome credit type ID. */
export const CURRENCY_TO_CREDIT_TYPE_ID: Record<string, string> = {
  usd: CREDIT_TYPE_USD_ID,
  eur: CREDIT_TYPE_EUR_ID,
  gbp: CREDIT_TYPE_GBP_ID,
};

// --- Accessors ---

function devOrProd<T>(dev: T, prod: T): T {
  return isDevelopment() ? dev : prod;
}

export const getCreditTypeAwuId = () =>
  devOrProd(DEV_CREDIT_TYPE_AWU_ID, PROD_CREDIT_TYPE_AWU_ID);

export const getCreditTypeProgrammaticUsdId = () =>
  devOrProd(DEV_CREDIT_TYPE_PROG_USD_ID, PROD_CREDIT_TYPE_PROG_USD_ID);

// Metrics
export const getMetricLlmProviderCostProgrammaticId = () =>
  devOrProd(
    DEV_METRIC_LLM_PROVIDER_COST_PROGRAMMATIC,
    PROD_METRIC_LLM_PROVIDER_COST_PROGRAMMATIC
  );
export const getMetricToolInvocationsId = () =>
  devOrProd(DEV_METRIC_TOOL_INVOCATIONS_V2, PROD_METRIC_TOOL_INVOCATIONS_V2);
export const getMetricLlmProviderCostAwuId = () =>
  devOrProd(
    DEV_METRIC_LLM_PROVIDER_COST_AWU_V2,
    PROD_METRIC_LLM_PROVIDER_COST_AWU_V2
  );

// Products
export const getProductProgrammaticUsageId = () =>
  devOrProd(DEV_PRODUCT_PROGRAMMATIC_USAGE, PROD_PRODUCT_PROGRAMMATIC_USAGE);
export const getProductAiUsageId = () =>
  devOrProd(DEV_PRODUCT_AI_USAGE, PROD_PRODUCT_AI_USAGE);
export const getProductToolUsageId = () =>
  devOrProd(DEV_PRODUCT_TOOL_USAGE, PROD_PRODUCT_TOOL_USAGE);
export const getProductWorkspaceSeatId = () =>
  devOrProd(DEV_PRODUCT_WORKSPACE_SEAT, PROD_PRODUCT_WORKSPACE_SEAT);
export const getProductProSeatId = () =>
  devOrProd(DEV_PRODUCT_PRO_SEAT, PROD_PRODUCT_PRO_SEAT);
export const getProductMaxSeatId = () =>
  devOrProd(DEV_PRODUCT_MAX_SEAT, PROD_PRODUCT_MAX_SEAT);
export const getProductFreeSeatId = () =>
  devOrProd(DEV_PRODUCT_FREE_SEAT, PROD_PRODUCT_FREE_SEAT);
export const getProductFreeCreditId = () =>
  devOrProd(DEV_PRODUCT_FREE_CREDITS, PROD_PRODUCT_FREE_CREDITS);
export const getProductExcessCreditsId = () =>
  devOrProd(DEV_PRODUCT_EXCESS_CREDITS, PROD_PRODUCT_EXCESS_CREDITS);
export const getProductPrepaidCommitId = () =>
  devOrProd(DEV_PRODUCT_PREPAID_COMMIT, PROD_PRODUCT_PREPAID_COMMIT);
export const getProductPlatformFeeId = () =>
  devOrProd(DEV_PRODUCT_PLATFORM_FEE, PROD_PRODUCT_PLATFORM_FEE);
export const getProductSeatSubscriptionCreditsId = () =>
  devOrProd(
    DEV_PRODUCT_SEAT_SUBSCRIPTION_CREDITS,
    PROD_PRODUCT_SEAT_SUBSCRIPTION_CREDITS
  );
export const getProductSeatSubscriptionCommitId = () =>
  devOrProd(
    DEV_PRODUCT_SEAT_SUBSCRIPTION_COMMIT,
    PROD_PRODUCT_SEAT_SUBSCRIPTION_COMMIT
  );

// AWU commit priorities — lower number is consumed first.
// Seat allocations are consumed before purchased top-ups.
export const AWU_PRIORITY_SEAT_ALLOCATION = 0;
// Free-seat per-user credits are a non-seat-based credit (created via
// `add_credits` with a `user_id` specifier, not a SEAT_BASED subscription).
// Metronome forbids a non-seat-based commit/credit from being prioritized over
// a seat-based one, so this MUST be strictly greater than
// `AWU_PRIORITY_SEAT_ALLOCATION`. Kept below `AWU_PRIORITY_PURCHASED_COMMIT` so
// a free user's free allowance is consumed before any purchased top-up.
export const AWU_PRIORITY_FREE_SEAT_CREDIT = 100;
export const AWU_PRIORITY_PURCHASED_COMMIT = 300;

// Per-seat AWU grant for free seats. Granted once per seat as a per-user
// contract credit at seat-assignment time (see `grantFreeSeatCredits`) rather
// than via a recurring credit, so this constant is also the authoritative
// source for the free seat's displayed allowance (see
// `getAwuAllocationInfoForSeatType`).
export const FREE_SEAT_LIFETIME_AWU_CREDITS = 500;

// Per-seat monthly AWU allocations for paid seat types. Stamped onto recurring
// credits at package creation and used as the authoritative displayed allowance.
export const PRO_SEAT_MONTHLY_AWU_CREDITS = 8000;
export const MAX_SEAT_MONTHLY_AWU_CREDITS = 40000;

// Seat commit/credit priorities
export const SEAT_PRIORITY_SUBSCRIPTION_COMMIT = 200;
export const SEAT_PRIORITY_COUPON_CREDIT = 300;

// 80% threshold for near-limit warnings: applies to both cap consumption
// (consumed ≥ 80% of effectiveCapAwuCredits) and seat depletion (≤ 20%
// remaining = 80% used).
export const NEAR_LIMIT_FRACTION = 0.8;
